import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Mountain, Loader2, MapPin, TrendingUp, Clock, Share2, Copy, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Custom POI marker (gold-bronze star)
const poiIcon = L.divIcon({
  className: '',
  html: '<div style="background:#B45309;color:#fff;width:24px;height:24px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:13px;box-shadow:0 2px 4px rgba(0,0,0,0.4)">★</div>',
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const FitBounds = ({ coords }) => {
  const map = useMap();
  useEffect(() => {
    if (!coords || coords.length < 2) return;
    const latlngs = coords.map(([lon, lat]) => [lat, lon]);
    map.fitBounds(latlngs, { padding: [40, 40] });
  }, [coords, map]);
  return null;
};

const PublicRoute = () => {
  const { routeId } = useParams();
  const [route, setRoute] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/routes/${routeId}`)
      .then(({ data }) => setRoute(data))
      .catch((err) => {
        setError(err.response?.status === 404 ? 'Rota não encontrada' : 'Erro a carregar rota');
      });
  }, [routeId]);

  const copyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copiado!');
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center p-8">
        <Mountain className="w-16 h-16 text-slate-500 mb-4" />
        <h1 className="text-2xl font-bold mb-2">{error}</h1>
        <p className="text-slate-400 text-sm mb-6">Verifica o link partilhado.</p>
        <Link to="/login" className="text-amber-400 underline">Voltar ao Tectur 4x4</Link>
      </div>
    );
  }

  if (!route) {
    return (
      <div className="min-h-screen bg-slate-900 text-white flex flex-col items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-amber-400" />
      </div>
    );
  }

  const polylineLatLngs = (route.coordinates || []).map(([lon, lat]) => [lat, lon]);
  const center = polylineLatLngs.length ? polylineLatLngs[0] : [40.32, -7.61];

  const diffColor =
    route.difficulty === 'Difícil' ? 'bg-orange-600' :
    route.difficulty === 'Moderada' ? 'bg-yellow-600' :
    'bg-emerald-600';

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="public-route-page">
      {/* Header */}
      <header className="bg-slate-800/90 backdrop-blur border-b border-slate-700">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center gap-3">
          <Mountain className="w-7 h-7 text-amber-400" />
          <div className="flex-1 min-w-0">
            <div className="text-xs text-slate-400">Tectur 4x4 · Rota partilhada</div>
            <h1 className="text-lg font-bold truncate" data-testid="public-route-name">{route.name}</h1>
          </div>
          <Button variant="outline" size="sm" onClick={copyLink} className="bg-slate-800 border-slate-600 text-white hover:bg-slate-700" data-testid="copy-share-link">
            <Copy className="w-4 h-4 mr-2" /> Copiar link
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 grid md:grid-cols-3 gap-4">
        {/* Map */}
        <div className="md:col-span-2 rounded-xl overflow-hidden border border-slate-700 shadow-2xl" style={{ minHeight: 520 }}>
          <MapContainer center={center} zoom={13} style={{ height: 520, width: '100%' }} scrollWheelZoom>
            <TileLayer
              attribution='© OpenStreetMap · © OpenTopoMap (CC-BY-SA) · SRTM'
              url="https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png"
              subdomains="abc"
              maxZoom={17}
            />
            {/* Esri World Hillshade overlay — reforça a volumetria das montanhas
                sobre o OpenTopoMap. Free, sem chave. Note: Esri usa /z/y/x. */}
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/Elevation/World_Hillshade/MapServer/tile/{z}/{y}/{x}"
              opacity={0.4}
              maxZoom={15}
              attribution="Hillshade © Esri · SRTM"
            />
            {polylineLatLngs.length > 1 && (
              <>
                <Polyline positions={polylineLatLngs} pathOptions={{ color: '#FFFFFF', weight: 9, opacity: 0.6 }} />
                <Polyline positions={polylineLatLngs} pathOptions={{ color: '#FF1493', weight: 5, opacity: 1 }} />
                <FitBounds coords={route.coordinates} />
              </>
            )}
            {(route.pois || []).map((p) => (
              <Marker key={p.id} position={[p.latitude, p.longitude]} icon={poiIcon}>
                <Popup>
                  <b>{p.name}</b>
                  {p.description && <p style={{ margin: '6px 0 0', fontSize: 12 }}>{p.description}</p>}
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {/* Info panel */}
        <div className="space-y-3">
          <div className="rounded-xl bg-slate-800 border border-slate-700 p-4">
            <div className="flex flex-wrap gap-2 mb-3">
              {route.difficulty && (
                <span className={`${diffColor} text-white text-xs font-bold px-3 py-1 rounded-full`}>
                  {route.difficulty}
                </span>
              )}
              {route.route_type && (
                <span className="bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                  {route.route_type === 'suv-4x4' ? 'SUV 4x4' : route.route_type === '4x4-reductoras' ? '4x4 Redutoras' : route.route_type}
                </span>
              )}
            </div>
            {route.description && (
              <p className="text-sm text-slate-300 leading-relaxed">{route.description}</p>
            )}
          </div>

          <div className="rounded-xl bg-slate-800 border border-slate-700 p-4 space-y-3">
            <Stat icon={<ArrowRight className="w-4 h-4" />} label="Distância" value={route.distance_km ? `${route.distance_km.toFixed(2)} km` : '—'} />
            <Stat icon={<TrendingUp className="w-4 h-4" />} label="Ganho elevação" value={route.elevation_gain_m ? `${Math.round(route.elevation_gain_m)} m` : '—'} />
            <Stat icon={<Clock className="w-4 h-4" />} label="Duração" value={route.duration_hours ? `${route.duration_hours.toFixed(1)} h` : '—'} />
            <Stat icon={<MapPin className="w-4 h-4" />} label="Pontos de interesse" value={`${route.pois?.length || 0} POIs`} />
          </div>

          <Link to="/login" className="block">
            <Button className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold" data-testid="open-in-tectur-cta">
              <Share2 className="w-4 h-4 mr-2" />
              Abrir no Tectur 4x4
            </Button>
          </Link>
          <p className="text-xs text-slate-500 text-center">
            Inicia sessão na app para descarregar o ficheiro GPX/KML e navegar offline.
          </p>
        </div>
      </div>

      <footer className="text-center text-xs text-slate-500 py-6">
        Mapa: © OpenStreetMap · OpenTopoMap (CC-BY-SA) · Hillshade Esri (SRTM)
      </footer>
    </div>
  );
};

const Stat = ({ icon, label, value }) => (
  <div className="flex items-center gap-3">
    <div className="w-8 h-8 rounded-full bg-slate-700 text-amber-400 flex items-center justify-center">
      {icon}
    </div>
    <div className="flex-1">
      <div className="text-[10px] text-slate-400 uppercase tracking-wide">{label}</div>
      <div className="text-sm font-bold">{value}</div>
    </div>
  </div>
);

export default PublicRoute;
